import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { Skeleton } from 'antd';
import {
    calculateFontSize,
    formatNumWithKMBAlt,
    getConfig,
    getMaxBound,
    getMinBound,
    removeFunctionFromProps,
} from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
const G2 = require('@antv/g2');

const defaultLabelWordLimit = 5;

//antv g2 doesnt render these string in tooltip and returns empty string,
//inorder to avoid them from displaying empty string, displaying alternative name
const replacementKeyMapping: any = {
    '<NA>': 'NA',
    '<na>': 'na',
};

const offsetXDistanceVar = -1 * calculateFontSize(window.innerWidth, 5);

//ADD ALL THE REQUIRED PROPS AT TOP
interface Props {
    fields: Array<string>;
    dataset: Array<any>;
    chartContainer: string;
    height: string | number;
    loading: boolean;
    colorKey: string;
    reflect?: boolean;
    showLoader?: boolean;
    yAxisConfig?: boolean | object;
    xAxisConfig?: boolean | object;
    chartConfig?: object;
    yAxisLabelFormatter?: (val: any) => any;
    xAxisLabelFormatter?: (val: any) => any;
    tooltipConfig?: object;
    YGrid?: boolean;
    XGrid?: boolean;
    padding?: [number, number, number, number];
    width?: number;
    barWidth?: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    error?: any;
    additionalStyle?: any;
    minBound?: number;
    maxBound?: number;
    pointMinBound?: number;
    pointMaxBound?: number;
    showLegend?: boolean;
    showErrorBox?: boolean;
    transpose?: boolean;
    Yline?: boolean;
    Xline?: boolean;
    showValueInTheMiddle?: boolean;

    enableGroupedChart?: boolean;
    enableGroupedPointChart?: boolean;
    intervalLabelTooltipKey?: string;
    groupedChartColor?: any[];
    groupedPointColor?: [string, string];
    groupedChartTooltip?: string;
    groupedPointChartTooltip?: string;
    intervalStyle?: object;
    disableIntervalLabel?: boolean;
    tooltipFormatter?: (xAxis: any, val: any) => any;
    intervalFormatter?: (val: any) => any;
    pointChartTooltipFormatter?: (xAxis: any, val: any) => any;
    addPercentageInIntervalLabel?: boolean;
    enablePointChartTooltip?: boolean;
    offsetXDistance?: number;
    range?: [number, number];
}

const BarChart: FC<Props> = ({
    yAxisConfig = true,
    xAxisConfig = true,
    chartConfig = {},
    height = 400,
    width,
    fields,
    xAxisTitle = '',
    yAxisTitle = '',
    colorKey = 'color',
    additionalStyle,
    dataset,
    chartContainer,
    tooltipConfig = {},
    loading,
    padding = [0, 0, 0, 0],
    error,
    barWidth = 20,
    YGrid = true,
    XGrid = false,
    Yline = false,
    Xline = true,
    showLoader = true,
    showErrorBox = true,
    minBound,
    showLegend = false,
    yAxisLabelFormatter,
    xAxisLabelFormatter,
    intervalLabelTooltipKey = fields[1],
    tooltipFormatter,
    intervalFormatter,
    pointChartTooltipFormatter,
    maxBound,
    pointMaxBound = 0,
    pointMinBound = 0,
    groupedChartTooltip = `${fields[2]}*${intervalLabelTooltipKey}`,
    groupedPointChartTooltip = `${fields[2]}*${fields[3]}`,
    transpose,
    showValueInTheMiddle = false,
    enableGroupedChart = false,
    enableGroupedPointChart = false,
    groupedChartColor = ['', ''],
    groupedPointColor = ['', ''],
    intervalStyle = {},
    disableIntervalLabel = false,
    addPercentageInIntervalLabel = false,
    reflect = false,
    offsetXDistance = offsetXDistanceVar,
    range = [0, 1],
    enablePointChartTooltip = false,
}) => {
    // if (!loading && error && dataset?.length === 0 && showErrorBox) {
    //     return <ErrorBox msg={String(error)} />;
    // }

    //this is used to track tab change and
    //re render the canvas element when tabs are changed.
    const isWindowTabActive = useTrackTabChange();

    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonSetting;
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;

    const tooltipFormatterFunc = (xAxis: any, val: any) => {
        if (tooltipFormatter) return tooltipFormatter(xAxis, val);
        return {
            name: xAxis,
            value: addPercentageInIntervalLabel
                ? formatNumWithKMBAlt(val, decimalForPercentage)
                : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
        };
    };

    const intervalFormatterFunc = (val: any) => {
        if (intervalFormatter) return intervalFormatter(val);
        return addPercentageInIntervalLabel
            ? formatNumWithKMBAlt(val, decimalForPercentage)
            : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
    };

    const pointChartTooltipFormatterFunc = (xAxis: any, val: any) => {
        if (pointChartTooltipFormatter) return pointChartTooltipFormatter(xAxis, val);
        return {
            name: xAxis,
            value: addPercentageInIntervalLabel
                ? formatNumWithKMBAlt(val, decimalForPercentage)
                : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
        };
    };
    useEffect(() => {
        if (!window) return;
        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
            inPlot: false,
            position: 'left',
        };

        const min = minBound ? minBound : getMinBound(dataset, fields);
        const max = maxBound ? maxBound : getMaxBound(dataset, fields);
        const chart = new G2.Chart({
            container: chartContainer,
            height: height,
            width: width,
            autoFit: width ? false : true,
            appendPadding: padding,
        });

        chart.data(dataset);

        chart.scale(fields[1], {
            nice: true,
            min: min,
            max: max,
            range: range,
            ...chartConfig,
        });

        //field 3 is for point grouped chart
        if (fields[3]) {
            chart.scale(fields[3], {
                nice: true,
                min: pointMinBound,
                max: pointMaxBound,
                ...chartConfig,
            });
            chart.axis(fields[3], false);
        }
        if (yAxisConfig) {
            chart.axis(fields[1], {
                style: {
                    textAlign: 'center',
                },
                label: !yAxisLabelFormatter
                    ? null
                    : {
                          formatter: yAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                line: Yline ? {} : null,
                grid: YGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !yAxisTitle
                    ? null
                    : {
                          text: yAxisTitle,
                          position: 'middle',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                position: 'left',
            });
        }
        if (!yAxisConfig) chart.axis(fields[1], false);
        if (!xAxisConfig) chart.axis(fields[0], false);
        if (xAxisConfig) {
            chart.axis(fields[0], {
                label: !xAxisLabelFormatter
                    ? null
                    : {
                          formatter: xAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                          //   offset: 0,
                      },
                tickLine: {
                    alignTick: true,
                },
                line: Xline ? {} : null,
                grid: XGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !xAxisTitle
                    ? null
                    : {
                          text: xAxisTitle,
                          position: 'start',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
            });
        }

        if (enableGroupedChart) {
            chart
                .interval()
                .position(`${fields[0]}*${fields[1]}`)
                .color(`${fields[2]}`, groupedChartColor)
                .adjust([
                    {
                        type: 'dodge',
                        //dodgeBy: 'category',
                        marginRatio: 0,
                    },
                ])
                .label(`${intervalLabelTooltipKey}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    return {
                        position: pos,
                        offsetY: val < 0 ? -10 : 10,
                        content: addPercentageInIntervalLabel
                            ? intervalFormatterFunc(val) + '%'
                            : intervalFormatterFunc(val),
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(groupedChartTooltip, (xAxis: any, val: any) => {
                    let temp = xAxis;
                    if (temp in replacementKeyMapping) temp = replacementKeyMapping[temp];
                    return tooltipFormatterFunc(temp, val);
                })
                .style({ ...intervalStyle });
        } else {
            chart
                .interval()
                .position(`${fields[0]}*${fields[1]}`)
                .color(`${colorKey}`, (color: any) => {
                    return color;
                })
                .size(barWidth)
                .label(`${intervalLabelTooltipKey}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos1 = 'middle';
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    const formattedValue = intervalFormatterFunc(val);
                    return {
                        position: showValueInTheMiddle ? pos1 : pos,
                        offsetX: transpose ? (val < 0 ? offsetXDistance * formattedValue.length : -8) : 0,
                        offsetY: transpose ? 0 : val < 0 ? -10 : 10,
                        content: addPercentageInIntervalLabel ? formattedValue + '%' : formattedValue,
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(`${fields[0]}*${intervalLabelTooltipKey}`, (xAxis: any, val: any) => {
                    let temp = xAxis;
                    if (temp in replacementKeyMapping) temp = replacementKeyMapping[temp];
                    return tooltipFormatterFunc(temp, val);
                })
                .style({ ...intervalStyle });
        }
        if (enableGroupedPointChart) {
            chart
                .point()
                .position(`${fields[0]}*${fields[3]}`)
                .color(`${fields[2]}`, groupedPointColor)
                .adjust([
                    {
                        type: 'dodge',
                        //dodgeBy: 'category',
                        marginRatio: 0,
                    },
                ])
                .shape('circle')
                .label(`${fields[3]}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    return {
                        position: pos,
                        offsetY: val < 0 ? 23 : 3,
                        content: addPercentageInIntervalLabel
                            ? formatNumWithKMBAlt(val, decimalForPercentage) + '%'
                            : formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(!enablePointChartTooltip ? false : groupedPointChartTooltip, (xAxis: any, val: any) => {
                    let temp = xAxis;
                    if (temp in replacementKeyMapping) temp = replacementKeyMapping[temp];
                    return pointChartTooltipFormatterFunc(temp, val);
                })
                .style({ ...intervalStyle });
        }
        if (transpose) chart.coordinate().transpose();
        if (reflect && transpose) chart.coordinate().transpose().reflect('x');
        //just for tooltip

        chart.tooltip({
            ...defaultTooltipConfig,
            ...tooltipConfig,
            containerTpl:
                '<div class="g2-tooltip">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
        });

        if (!showLegend) chart.legend(false);
        else
            chart.legend({
                position: 'top',
                layout: 'horizontal',
            });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, width, isWindowTabActive, disableIntervalLabel]);

    return (
        <div className="">
            {showLoader && (
                <>
                    <Skeleton loading={loading} />
                    <Skeleton loading={loading} />
                </>
            )}
            <div style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }} id={chartContainer} />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps }, ['tooltipConfig']); //remove function type from props for referential equality
    return _.isEqual(clonePrev, cloneNext);
}

export default React.memo(BarChart, arePropsEqual);
